

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleGenAI, Chat, GenerateContentResponse } from "@google/genai";
import { Button } from './Button';

// Helper to get API key (ensure this is secure and ideally not exposed client-side in production)
const getApiKey = () => {
  if (typeof process === 'undefined' || !process.env || !process.env.API_KEY) {
    console.warn("API_KEY not found in process.env. Using a placeholder for demonstration if necessary, but API calls will likely fail.");
    return undefined;
  }
  return process.env.API_KEY;
};

const GEMINI_MODEL_NAME = "gemini-2.5-flash-preview-04-17";
const SYSTEM_INSTRUCTION = "You are an expert Apple product troubleshooter. Help technicians diagnose customer issues with iPhones, iPads, MacBooks, Apple Watch, and other Apple devices. Provide clear, step-by-step solutions. If a problem is complex or likely hardware-related, advise seeking advanced technical support or an authorized repair. Do not reveal you are an AI. Format responses clearly, using markdown for things like lists or bolding key terms if appropriate.";

interface Message {
  role: 'user' | 'model';
  text: string;
  id: string;
}

const SparklesIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L1.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.25 12L17 14.187l-1.25-2.187a3.375 3.375 0 00-2.682-2.682L10.813 9l2.187-1.25a3.375 3.375 0 002.682-2.682L17 1.813l1.25 2.187a3.375 3.375 0 002.682 2.682L23.187 9l-2.187 1.25a3.375 3.375 0 00-2.682 2.682z" />
    </svg>
);

const PaperAirplaneIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
  </svg>
);


export const AITroubleshooterPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chat, setChat] = useState<Chat | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const apiKey = useRef(getApiKey());

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const initializeChat = useCallback(() => {
    if (!apiKey.current) {
      setError("API Key is not configured. Cannot initialize AI chat.");
      console.error("API Key is missing for Gemini Chat.");
      return null;
    }
    try {
      const ai = new GoogleGenAI({ apiKey: apiKey.current });
      const newChat = ai.chats.create({
        model: GEMINI_MODEL_NAME,
        config: {
          systemInstruction: SYSTEM_INSTRUCTION,
        },
      });
      setChat(newChat);
      setError(null); // Clear previous errors
      return newChat;
    } catch (e) {
      console.error("Failed to initialize Gemini Chat:", e);
      setError("Failed to initialize AI chat. Please check your API key and configuration.");
      return null;
    }
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const currentInput = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    setError(null);

    const userMessage: Message = { role: 'user', text: currentInput, id: Date.now().toString() };
    setMessages(prev => [...prev, userMessage]);
    
    let currentChat = chat;
    if (!currentChat) {
        currentChat = initializeChat();
        if(!currentChat) {
            setIsLoading(false);
            // Error is already set by initializeChat
            return;
        }
    }

    // FIX: Declare modelMessageId in a scope accessible by both try and catch blocks
    let modelMessageId: string | null = null;

    try {
      const stream = await currentChat.sendMessageStream({ message: currentInput });
      let currentModelResponse = '';
      modelMessageId = (Date.now() + 1).toString(); 

      // Add a placeholder for the model's response
      setMessages(prev => [...prev, { role: 'model', text: '', id: modelMessageId! }]);

      for await (const chunk of stream) { // chunk type is GenerateContentResponse
        currentModelResponse += chunk.text;
        setMessages(prev => prev.map(msg => 
          msg.id === modelMessageId ? { ...msg, text: currentModelResponse } : msg
        ));
      }
    } catch (e) {
      console.error("Error sending message to Gemini:", e);
      setError("Sorry, something went wrong while contacting the AI. Please try again.");
      // Remove the placeholder if there was an error during streaming
      // FIX: modelMessageId is now in scope. Added null check before use.
      if (modelMessageId) {
        setMessages(prev => prev.filter(msg => msg.id !== modelMessageId));
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initialize chat on first load if API key exists
  useEffect(() => {
    if(apiKey.current && !chat) {
        initializeChat();
    }
  }, [initializeChat, chat]);


  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-var(--header-height,64px)-var(--footer-height,56px)-3rem)]"> {/* Adjust height based on parent layout */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-4">
        <div className="flex items-center">
          <SparklesIcon className="w-8 h-8 text-[#003794] mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-[#003794]">AI Apple Product Troubleshooter</h1>
            <p className="text-sm text-slate-600">Ask questions about Apple product issues for troubleshooting help.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-md" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      {!apiKey.current && !error && (
         <div className="bg-amber-100 border-l-4 border-amber-500 text-amber-700 p-4 mb-4 rounded-md" role="alert">
          <p className="font-bold">Configuration Notice</p>
          <p>The Gemini API key is not configured. The AI Troubleshooter will not be functional.</p>
        </div>
      )}


      <div ref={chatContainerRef} className="flex-grow bg-white p-4 sm:p-6 rounded-lg shadow-inner overflow-y-auto space-y-4 mb-4">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[70%] p-3 rounded-xl shadow ${
                msg.role === 'user' 
                ? 'bg-[#003794] text-white rounded-br-none' 
                : 'bg-slate-200 text-slate-800 rounded-bl-none'
            }`}>
              {/* Basic markdown rendering for newlines and bold */}
              {msg.text.split('\n').map((line, index) => (
                <span key={index}>
                  {line.split(/(\*\*.*?\*\*)/g).map((part, i) => 
                    part.startsWith('**') && part.endsWith('**') ? 
                    <strong key={i}>{part.slice(2, -2)}</strong> : 
                    part
                  )}
                  {index < msg.text.split('\n').length - 1 && <br />}
                </span>
              ))}
            </div>
          </div>
        ))}
        {isLoading && messages[messages.length -1]?.role === 'user' && (
          <div className="flex justify-start">
            <div className="max-w-[70%] p-3 rounded-xl shadow bg-slate-200 text-slate-800 rounded-bl-none">
              <div className="flex items-center">
                <div className="animate-pulse flex space-x-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-slate-400 rounded-full"></div>
                </div>
                <span className="ml-2 text-sm italic">AI is thinking...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mt-auto">
        <div className="flex items-center space-x-3">
          <textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder={apiKey.current ? "Type your question about an Apple product..." : "AI Chat disabled (API Key missing)"}
            className="flex-grow p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#003794] focus:border-[#003794] resize-none"
            rows={2}
            disabled={isLoading || !apiKey.current}
            aria-label="Chat input"
          />
          <Button
            onClick={handleSendMessage}
            disabled={isLoading || !inputValue.trim() || !apiKey.current}
            variant="primary"
            className="h-full px-5 py-3 flex items-center justify-center"
            aria-label="Send message"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
